import { UrlSlug, urlSlugToDataProductKey } from '@/analytics/mappings/dataProductKey.ts';

import {
	BitbucketPremiumPriceCard,
	BitbucketStandardPriceCard,
} from './components/bitbucket-price-card.tsx';
import {
	JiraProductDiscoveryFreePriceCard,
	JiraProductDiscoveryStandardPriceCard,
	JiraProductDiscoveryPremiumPriceCard,
	JiraProductDiscoveryEnterprisePriceCard,
} from './components/jira-product-discovery-price-card';
import {
	LoomFreePriceCard,
	LoomBusinessPriceCard,
	LoomBusinessPlusAiPriceCard,
	LoomEnterprisePriceCard,
} from './components/loom-price-card';
import {
	FreePriceCard,
	PremiumPriceCard,
	EnterprisePriceCard,
	StandardPriceCard,
} from './components/pricing-calculator-plan-card.tsx';
import { RovoDevStandardPriceCard } from './components/rovo-dev-standard-price-card';
import {
	ServiceCollectionFreePriceCard,
	ServiceCollectionStandardPriceCard,
	ServiceCollectionPremiumPriceCard,
	ServiceCollectionEnterprisePriceCard,
} from './components/service-collection-price-card';
import { bitbucketOfferingSkus } from './configs/bitbucket.pricing-calculator-config.ts';
import { compassOfferingSkus } from './configs/compass.pricing-calculator-config.ts';
import { confluenceOfferingSkus } from './configs/confluence.pricing-calculator-config.ts';
import { guardOfferingSkus } from './configs/guard.pricing-calculator-config.ts';
import { jiraOfferingSkus } from './configs/jira.pricing-calculator-config.ts';
import { jpdOfferingSkus } from './configs/jpd.pricing-calculator-config.ts';
import { loomOfferingSkus } from './configs/loom.pricing-calculator-config.ts';
import { rovoDevOfferingSkus } from './configs/rovo-dev.pricing-calculator-config.ts';
import { serviceCollectionOfferingSkus } from './configs/service-collection.pricing-calculator-config.ts';
import { strategyCollectionOfferingSkus } from './configs/strategy-collection.pricing-calculator-config.ts';
import { teamworkCollectionOfferingSkus } from './configs/teamwork-collection.pricing-calculator-config.ts';
import { Plan } from './types.ts';

export interface PricingCalculatorCardProps {
	planData: Plan;
	urlSlug: UrlSlug;
	topSectionStyle?: React.CSSProperties;
	bottomSectionStyle?: React.CSSProperties;
	index: number;
	showBannerText?: boolean;
	isSingleCard?: boolean;
}

// eslint-disable-next-line complexity
export const PricingCalculatorCard = (props: PricingCalculatorCardProps) => {
	const sku = props.planData.fields.sku;
	const trackingProductKey = urlSlugToDataProductKey[props.urlSlug];

	switch (sku) {
		// Free
		case jiraOfferingSkus.free:
		case confluenceOfferingSkus.free:
		case teamworkCollectionOfferingSkus.free:
		case compassOfferingSkus.free:
		case bitbucketOfferingSkus.free:
			return <FreePriceCard {...props} trackingProductKey={trackingProductKey} />;

		// Standard
		case jiraOfferingSkus.standard:
		case confluenceOfferingSkus.standard:
		case teamworkCollectionOfferingSkus.standard:
		case guardOfferingSkus.standard:
		case compassOfferingSkus.standard:
			return <StandardPriceCard {...props} trackingProductKey={trackingProductKey} />;
		case bitbucketOfferingSkus.standard:
			return <BitbucketStandardPriceCard {...props} trackingProductKey={trackingProductKey} />;

		// Premium
		case jiraOfferingSkus.premium:
		case confluenceOfferingSkus.premium:
		case teamworkCollectionOfferingSkus.premium:
		case guardOfferingSkus.premium:
		case compassOfferingSkus.premium:
			return <PremiumPriceCard {...props} trackingProductKey={trackingProductKey} />;
		case bitbucketOfferingSkus.premium:
			return <BitbucketPremiumPriceCard {...props} trackingProductKey={trackingProductKey} />;
		// Enterprise
		case jiraOfferingSkus.enterprise:
		case confluenceOfferingSkus.enterprise:
		case teamworkCollectionOfferingSkus.enterprise:
			// @ts-ignore
			return <EnterprisePriceCard {...props} />;
		case strategyCollectionOfferingSkus.enterprise:
			// @ts-ignore
			return <EnterprisePriceCard {...props} isJpyPriceEnabled />;

		// Custom, for any tier
		case rovoDevOfferingSkus.standard:
			return <RovoDevStandardPriceCard {...props} trackingProductKey={trackingProductKey} />;

		case serviceCollectionOfferingSkus.free:
			return <ServiceCollectionFreePriceCard {...props} trackingProductKey={trackingProductKey} />;
		case serviceCollectionOfferingSkus.standard:
			return (
				<ServiceCollectionStandardPriceCard {...props} trackingProductKey={trackingProductKey} />
			);
		case serviceCollectionOfferingSkus.premium:
			return (
				<ServiceCollectionPremiumPriceCard {...props} trackingProductKey={trackingProductKey} />
			);
		case serviceCollectionOfferingSkus.enterprise:
			return (
				<ServiceCollectionEnterprisePriceCard {...props} trackingProductKey={trackingProductKey} />
			);

		case loomOfferingSkus.free:
			return <LoomFreePriceCard {...props} trackingProductKey={trackingProductKey} />;
		case loomOfferingSkus.business:
			return <LoomBusinessPriceCard {...props} trackingProductKey={trackingProductKey} />;
		case loomOfferingSkus.businessPlusAi:
			return <LoomBusinessPlusAiPriceCard {...props} trackingProductKey={trackingProductKey} />;
		case loomOfferingSkus.enterprise:
			return <LoomEnterprisePriceCard {...props} trackingProductKey={trackingProductKey} />;

		case jpdOfferingSkus.free:
			return (
				<JiraProductDiscoveryFreePriceCard {...props} trackingProductKey={trackingProductKey} />
			);

		case jpdOfferingSkus.standard:
			return (
				<JiraProductDiscoveryStandardPriceCard {...props} trackingProductKey={trackingProductKey} />
			);

		case jpdOfferingSkus.premium:
			return (
				<JiraProductDiscoveryPremiumPriceCard {...props} trackingProductKey={trackingProductKey} />
			);
		case jpdOfferingSkus.enterprise:
			return (
				<JiraProductDiscoveryEnterprisePriceCard
					{...props}
					trackingProductKey={trackingProductKey}
				/>
			);

		default:
			// todo: @bsparks GWP-6943 fire an event for unexpected sku
			return null;
	}
};
